import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, apiUpload, ApiError } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import {
  CLINICAL_CONCERNS, GENDER_PREFERENCES,
  INSURANCE_TYPES, INSURANCE_CARRIERS, ACCEPTING_NEW_INSURANCE_PATIENTS,
  SERVICE_TYPES, WEEKDAYS, CALENDAR_CONNECTOR_TYPES, IMPLEMENTED_CALENDAR_CONNECTORS,
  CALENDAR_CONNECTOR_LABELS,
} from '../../lib/vocab'
import type { CalendarConnection, Practice, Provider, ProviderDocument, ProviderLocation } from '../../lib/types'

type Step =
  | 'account' | 'professional' | 'clinical_profile' | 'practice_information'
  | 'insurance_pricing' | 'scheduling_availability' | 'review'

const STEPS: Step[] = [
  'account', 'professional', 'clinical_profile', 'practice_information',
  'insurance_pricing', 'scheduling_availability', 'review',
]

const STEP_LABELS: Record<Step, string> = {
  account: 'Account Information',
  professional: 'Professional Information',
  clinical_profile: 'Clinical Profile',
  practice_information: 'Practice Information',
  insurance_pricing: 'Insurance & Pricing',
  scheduling_availability: 'Scheduling & Availability',
  review: 'Review & Submit',
}

const AGE_OPTIONS = ['child', 'teen', 'adult', 'older_adult']
const AGE_LABELS: Record<string, string> = {
  child: 'Children (0–12)',
  teen: 'Teens (13–17)',
  adult: 'Adults',
  older_adult: 'Older Adults',
}
const DOCUMENT_TYPES: { value: string; label: string; required: boolean }[] = [
  { value: 'license', label: 'Professional license', required: true },
  { value: 'resume', label: 'Resume', required: false },
  { value: 'board_certification', label: 'Board certification', required: false },
  { value: 'malpractice_insurance', label: 'Malpractice insurance', required: false },
]

function splitCsv(v: string) {
  return v.split(',').map((x) => x.trim()).filter(Boolean)
}

function nextStep(current: Step): Step {
  const i = STEPS.indexOf(current)
  return STEPS[Math.min(i + 1, STEPS.length - 1)]
}

export default function ProviderOnboardingWizard() {
  const { me, refreshMe } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('account')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [practices, setPractices] = useState<Practice[]>([])
  const stepHeadingRef = useRef<HTMLDivElement>(null)

  // Same pattern as PatientIntakeWizard: move focus to the step indicator on
  // every step change so Back/Continue never strands keyboard focus, and
  // aria-live announces it for screen readers even without a focus move.
  useEffect(() => {
    stepHeadingRef.current?.focus()
  }, [step])

  const [name, setName] = useState('')
  const [bio, setBio] = useState('')
  const [credentials, setCredentials] = useState('')
  const [yearsOfExperience, setYearsOfExperience] = useState('')
  const [languages, setLanguages] = useState('')
  const [specialties, setSpecialties] = useState<string[]>([])
  const [modalities, setModalities] = useState('')
  const [acceptedAges, setAcceptedAges] = useState<string[]>([])
  const [genderSpecialties, setGenderSpecialties] = useState<string[]>([])
  const [npiNumber, setNpiNumber] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [licenseState, setLicenseState] = useState('')
  const [licenseType, setLicenseType] = useState('')
  const [licenseExpirationDate, setLicenseExpirationDate] = useState('')
  const [practiceId, setPracticeId] = useState('')
  const [sessionFormat, setSessionFormat] = useState('either')
  const [capacityStatus, setCapacityStatus] = useState('open')
  const [acceptingNewClients, setAcceptingNewClients] = useState(true)

  // Insurance
  const [insuranceTypesAccepted, setInsuranceTypesAccepted] = useState<string[]>([])
  const [insuranceAccepted, setInsuranceAccepted] = useState<string[]>([])
  const [acceptingNewInsurancePatients, setAcceptingNewInsurancePatients] = useState('yes')

  // Self-pay fees
  const [selfPayPriceMin, setSelfPayPriceMin] = useState('')
  const [selfPayPriceMax, setSelfPayPriceMax] = useState('')
  const [intakeSessionFee, setIntakeSessionFee] = useState('')
  const [slidingScaleAvailable, setSlidingScaleAvailable] = useState(false)
  const [acceptingSelfPay, setAcceptingSelfPay] = useState(true)

  // Primary office location + service radius/virtual/service types
  const [country, setCountry] = useState('')
  const [stateProvince, setStateProvince] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [serviceRadiusKm, setServiceRadiusKm] = useState('')
  const [virtualOnly, setVirtualOnly] = useState(false)
  const [serviceTypes, setServiceTypes] = useState<string[]>([])

  // Additional office locations
  const [locations, setLocations] = useState<ProviderLocation[]>([])
  const [newLocCity, setNewLocCity] = useState('')
  const [newLocState, setNewLocState] = useState('')
  const [newLocCountry, setNewLocCountry] = useState('')
  const [newLocPostal, setNewLocPostal] = useState('')
  const [newLocStreet, setNewLocStreet] = useState('')
  const [addingLocation, setAddingLocation] = useState(false)

  // Availability + capacity
  const [waitlistEnabled, setWaitlistEnabled] = useState(false)
  const [averageWaitTimeDays, setAverageWaitTimeDays] = useState('')
  const [maxActiveCaseload, setMaxActiveCaseload] = useState('')
  const [workingDays, setWorkingDays] = useState<string[]>([])
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState('50')
  const [maxSessionsPerDay, setMaxSessionsPerDay] = useState('')
  const [maxSessionsPerWeek, setMaxSessionsPerWeek] = useState('')
  const [workStartHour, setWorkStartHour] = useState('9')
  const [workEndHour, setWorkEndHour] = useState('17')
  const [breakDurationMinutes, setBreakDurationMinutes] = useState('')
  const [pauseNewPatients, setPauseNewPatients] = useState(false)
  const [timeOffDates, setTimeOffDates] = useState<{ start_date: string; end_date: string; label: string }[]>([])
  const [newTimeOffStart, setNewTimeOffStart] = useState('')
  const [newTimeOffEnd, setNewTimeOffEnd] = useState('')
  const [newTimeOffLabel, setNewTimeOffLabel] = useState('')

  // Calendar connectivity (ICS only this phase)
  const [icsUrl, setIcsUrl] = useState('')
  const [calendarConnected, setCalendarConnected] = useState(false)
  const [calendarSyncNotice, setCalendarSyncNotice] = useState<string | null>(null)

  // Document uploads
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState<string | null>(null)
  const [credentialConsent, setCredentialConsent] = useState(false)
  const [calendarConsent, setCalendarConsent] = useState(false)

  useEffect(() => {
    if (!me?.provider_id) return
    Promise.all([
      api(`/providers/${me.provider_id}`),
      api('/practices'),
      api(`/providers/${me.provider_id}/locations`),
      api(`/providers/${me.provider_id}/calendar-connections`),
      api(`/providers/${me.provider_id}/documents`),
    ]).then(([provider, pracs, locs, connections, docs]: [Provider, Practice[], ProviderLocation[], CalendarConnection[], ProviderDocument[]]) => {
      setName(provider.name || '')
      setBio(provider.bio || '')
      setCredentials(provider.credentials || '')
      setYearsOfExperience(provider.years_of_experience != null ? String(provider.years_of_experience) : '')
      setLanguages((provider.languages || []).join(', '))
      setSpecialties(provider.specialties || [])
      setModalities((provider.modalities || []).join(', '))
      setAcceptedAges(provider.accepted_ages || [])
      setGenderSpecialties(provider.gender_specialties || [])
      setNpiNumber(provider.npi_number || '')
      setLicenseNumber(provider.license_number || '')
      setLicenseState(provider.license_state || '')
      setLicenseType(provider.license_type || '')
      setLicenseExpirationDate(provider.license_expiration_date ? provider.license_expiration_date.slice(0, 10) : '')
      setPracticeId(provider.practice_id || '')
      setSessionFormat(provider.session_format || 'either')
      setCapacityStatus(provider.capacity_status || 'open')
      setAcceptingNewClients(provider.accepting_new_clients ?? true)
      setInsuranceTypesAccepted(provider.insurance_types_accepted || [])
      setInsuranceAccepted(provider.insurance_accepted || [])
      setAcceptingNewInsurancePatients(provider.accepting_new_insurance_patients || 'yes')
      setSelfPayPriceMin(provider.self_pay_price_min_cents != null ? String(provider.self_pay_price_min_cents / 100) : '')
      setSelfPayPriceMax(provider.self_pay_price_max_cents != null ? String(provider.self_pay_price_max_cents / 100) : '')
      setIntakeSessionFee(provider.intake_session_fee_cents != null ? String(provider.intake_session_fee_cents / 100) : '')
      setSlidingScaleAvailable(provider.sliding_scale_available ?? false)
      setAcceptingSelfPay(provider.accepting_self_pay ?? true)
      setCountry(provider.country || '')
      setStateProvince(provider.state_province || '')
      setCity(provider.city || '')
      setPostalCode(provider.postal_code || '')
      setServiceRadiusKm(provider.service_radius_km != null ? String(provider.service_radius_km) : '')
      setVirtualOnly(provider.virtual_only ?? false)
      setServiceTypes(provider.service_types || [])
      setWaitlistEnabled(provider.waitlist_enabled ?? false)
      setAverageWaitTimeDays(provider.average_wait_time_days != null ? String(provider.average_wait_time_days) : '')
      setMaxActiveCaseload(provider.max_active_caseload != null ? String(provider.max_active_caseload) : '')
      setWorkingDays(provider.working_days || [])
      setSessionDurationMinutes(provider.session_duration_minutes ? String(provider.session_duration_minutes) : '50')
      setMaxSessionsPerDay(provider.max_sessions_per_day != null ? String(provider.max_sessions_per_day) : '')
      setMaxSessionsPerWeek(provider.max_sessions_per_week != null ? String(provider.max_sessions_per_week) : '')
      setWorkStartHour(provider.work_start_hour != null ? String(provider.work_start_hour) : '9')
      setWorkEndHour(provider.work_end_hour != null ? String(provider.work_end_hour) : '17')
      setBreakDurationMinutes(provider.break_duration_minutes != null ? String(provider.break_duration_minutes) : '')
      setPauseNewPatients(provider.pause_new_patients ?? false)
      setTimeOffDates((provider.time_off_dates || []).map((t) => ({ start_date: t.start_date, end_date: t.end_date, label: t.label || '' })))
      setPractices(pracs)
      setLocations(locs)
      // Hydrate from the real connection state, not just in-session actions —
      // otherwise every time this wizard is revisited (resume, or "Continue
      // setup" from the dashboard) it would falsely show "Not connected" for
      // a provider who already connected their calendar in an earlier session.
      const connected = connections.find((c) => c.status === 'connected')
      if (connected) setCalendarConnected(true)
      // Same hydration gap as calendar connections above — without this, a
      // provider who already uploaded their license in an earlier session
      // would see it as missing every time they revisit this step.
      const docsByType: Record<string, string> = {}
      for (const d of docs) if (!docsByType[d.document_type]) docsByType[d.document_type] = d.filename
      setUploadedDocs(docsByType)
      // Resume where the provider left off — server-persisted, so this
      // survives a closed browser, lost connection, or full sign-out/sign-in,
      // not just an in-tab refresh.
      setStep((provider.onboarding_step as Step) || 'account')
      setLoading(false)
    })
  }, [me?.provider_id])

  function toggle(list: string[], setList: (v: string[]) => void, value: string) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])
  }

  async function saveAndAdvance(patch: Record<string, unknown>, to: Step) {
    setBusy(true)
    setError(null)
    try {
      await api(`/providers/${me!.provider_id}`, { method: 'PATCH', body: JSON.stringify({ ...patch, onboarding_step: to }) })
      setStep(to)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save — try again.')
    } finally {
      setBusy(false)
    }
  }

  async function saveLicenseAndAdvance() {
    setBusy(true)
    setError(null)
    try {
      await api(`/providers/${me!.provider_id}/credentialing`, {
        method: 'PATCH',
        body: JSON.stringify({
          npi_number: npiNumber || null,
          license_number: licenseNumber || null,
          license_state: licenseState || null,
          license_type: licenseType || null,
          license_expiration_date: licenseExpirationDate || null,
        }),
      })
      await api(`/providers/${me!.provider_id}`, { method: 'PATCH', body: JSON.stringify({ onboarding_step: 'clinical_profile' }) })
      setStep('clinical_profile')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save — try again.')
    } finally {
      setBusy(false)
    }
  }

  async function uploadDocument(documentType: string, file: File) {
    setUploading(documentType)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('document_type', documentType)
      formData.append('file', file)
      const result = await apiUpload(`/providers/${me!.provider_id}/documents`, formData)
      setUploadedDocs((prev) => ({ ...prev, [documentType]: result.filename }))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload — try again.')
    } finally {
      setUploading(null)
    }
  }

  async function addLocation() {
    setAddingLocation(true)
    setError(null)
    try {
      const created = await api(`/providers/${me!.provider_id}/locations`, {
        method: 'POST',
        body: JSON.stringify({
          country: newLocCountry || null, state_province: newLocState || null,
          city: newLocCity || null, postal_code: newLocPostal || null, street_address: newLocStreet || null,
        }),
      })
      setLocations((prev) => [...prev, created])
      setNewLocCity(''); setNewLocState(''); setNewLocCountry(''); setNewLocPostal(''); setNewLocStreet('')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add location — try again.')
    } finally {
      setAddingLocation(false)
    }
  }

  async function removeLocation(locationId: string) {
    setError(null)
    try {
      await api(`/providers/${me!.provider_id}/locations/${locationId}`, { method: 'DELETE' })
      setLocations((prev) => prev.filter((l) => l.id !== locationId))
    } catch {
      setError('Could not remove location — try again.')
    }
  }

  function addTimeOff() {
    if (!newTimeOffStart || !newTimeOffEnd) return
    setTimeOffDates((prev) => [...prev, { start_date: newTimeOffStart, end_date: newTimeOffEnd, label: newTimeOffLabel }])
    setNewTimeOffStart(''); setNewTimeOffEnd(''); setNewTimeOffLabel('')
  }

  function removeTimeOff(index: number) {
    setTimeOffDates((prev) => prev.filter((_, i) => i !== index))
  }

  async function connectCalendar() {
    setBusy(true)
    setError(null)
    try {
      const connection = await api(`/providers/${me!.provider_id}/calendar-connections`, {
        method: 'POST',
        body: JSON.stringify({ connector_type: 'ics', ics_url: icsUrl }),
      })
      const syncResult = await api(`/providers/${me!.provider_id}/calendar-connections/${connection.id}/sync`, { method: 'POST' })
      setCalendarConnected(true)
      setCalendarSyncNotice(`Connected — imported ${syncResult.busy_blocks_synced} busy block(s) from your calendar.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not connect your calendar — check the URL and try again.')
    } finally {
      setBusy(false)
    }
  }

  async function finish() {
    setBusy(true)
    setError(null)
    try {
      await api(`/providers/${me!.provider_id}/complete-onboarding`, { method: 'POST' })
      // The route gate reads `me.provider_onboarding_complete` from the
      // AuthContext cache, not a fresh fetch — without this, navigating to
      // the dashboard would immediately bounce back here on stale data.
      await refreshMe()
      navigate('/practice/provider', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not finish setup — try again.')
    } finally {
      setBusy(false)
    }
  }

  function back() {
    setError(null)
    const i = STEPS.indexOf(step)
    if (i > 0) setStep(STEPS[i - 1])
  }

  if (loading) return <div className="page-loading">Loading your profile…</div>

  const stepIndex = STEPS.indexOf(step)

  return (
    <div className="warm-theme">
      <div className="page-head">
        <h1>Set up your professional profile</h1>
        <div className="ph-sub" ref={stepHeadingRef} tabIndex={-1} aria-live="polite">{STEP_LABELS[step]} — step {stepIndex + 1} of {STEPS.length}. Your progress is saved as you go, so you can pick up right where you left off.</div>
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        {step === 'account' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>Let's start with the basics — how patients and your practice will see you.</p>
            <div className="field"><label>Your name</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} required /></div>
            <div className="field"><label>Email</label><input className="inp" value={me?.email || ''} disabled /></div>
            {practices.length > 0 && (
              <div className="field">
                <label>Practice / team</label>
                <select className="inp" value={practiceId} onChange={(e) => setPracticeId(e.target.value)}>
                  <option value="">— Not assigned —</option>
                  {practices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="btn full" disabled={busy} onClick={() => saveAndAdvance({ name, practice_id: practiceId || null }, nextStep(step))}>{busy ? 'Saving…' : 'Continue'}</button>
          </div>
        )}

        {step === 'professional' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>Help us verify your professional credentials. This stays with you — your practice and ATUUNE review it, it's never shown to patients directly.</p>
            <div className="field"><label>Credentials / title</label><input className="inp" value={credentials} onChange={(e) => setCredentials(e.target.value)} placeholder="LCSW, RP, MD…" /></div>
            <div className="form-grid">
              <div className="field"><label>NPI number (optional)</label><input className="inp" value={npiNumber} onChange={(e) => setNpiNumber(e.target.value)} placeholder="10-digit NPI" /></div>
              <div className="field"><label>License number</label><input className="inp" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label>License state</label><input className="inp" value={licenseState} onChange={(e) => setLicenseState(e.target.value)} placeholder="AB" /></div>
              <div className="field"><label>License type</label><input className="inp" value={licenseType} onChange={(e) => setLicenseType(e.target.value)} placeholder="LCSW, LPC, PsyD…" /></div>
            </div>
            <div className="field"><label>License expiration date</label><input className="inp" type="date" value={licenseExpirationDate} onChange={(e) => setLicenseExpirationDate(e.target.value)} /></div>

            <h3 style={{ marginBottom: 6, marginTop: 18 }}>Credential documents</h3>
            <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>Your account won't become bookable until at least your license is on file.</p>
            <div className="field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
              <input type="checkbox" id="credentialConsent" checked={credentialConsent} onChange={(e) => setCredentialConsent(e.target.checked)} style={{ marginTop: 3 }} />
              <label htmlFor="credentialConsent" style={{ margin: 0, fontSize: 13 }}>
                I consent to ATUUNE and my practice storing and reviewing these documents for credentialing purposes.
              </label>
            </div>
            <div className="field">
              {DOCUMENT_TYPES.map((d) => (
                <div key={d.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ minWidth: 170, fontSize: 13.5 }}>{d.label}{d.required ? ' *' : ''}</span>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    disabled={uploading === d.value || !credentialConsent}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadDocument(d.value, file) }}
                  />
                  {uploading === d.value && <span className="muted" style={{ fontSize: 12 }}>Uploading…</span>}
                  {uploadedDocs[d.value] && <span className="badge badge-green" style={{ fontSize: 11 }}>Uploaded</span>}
                </div>
              ))}
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" disabled={busy} onClick={back}>Back</button>
              <button className="btn full" disabled={busy} onClick={saveLicenseAndAdvance}>{busy ? 'Saving…' : 'Continue'}</button>
            </div>
          </div>
        )}

        {step === 'clinical_profile' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>What you treat and who you work with.</p>
            <div className="field">
              <label id="specialties-label">Presenting concerns treated</label>
              <div className="chip-list" role="group" aria-labelledby="specialties-label">
                {CLINICAL_CONCERNS.map((s) => (
                  <button type="button" key={s.value} className="chip" aria-pressed={specialties.includes(s.value)} style={{ background: specialties.includes(s.value) ? 'var(--warm-terracotta)' : '#F0EBE0', color: specialties.includes(s.value) ? 'white' : 'var(--warm-sage)' }} onClick={() => toggle(specialties, setSpecialties, s.value)}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field"><label>Treatment modalities (comma separated)</label><input className="inp" value={modalities} onChange={(e) => setModalities(e.target.value)} placeholder="CBT, DBT, EMDR" /></div>
            <div className="field">
              <label id="ages-label">Age groups served</label>
              <div className="chip-list" role="group" aria-labelledby="ages-label">
                {AGE_OPTIONS.map((a) => (
                  <button type="button" key={a} className="chip" aria-pressed={acceptedAges.includes(a)} style={{ background: acceptedAges.includes(a) ? 'var(--warm-terracotta)' : '#F0EBE0', color: acceptedAges.includes(a) ? 'white' : 'var(--warm-sage)' }} onClick={() => toggle(acceptedAges, setAcceptedAges, a)}>
                    {AGE_LABELS[a] ?? a.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div className="field"><label>Languages (comma separated)</label><input className="inp" value={languages} onChange={(e) => setLanguages(e.target.value)} placeholder="English" /></div>
            <div className="field">
              <label id="gender-specialties-label">Gender specialties (if applicable)</label>
              <div className="chip-list" role="group" aria-labelledby="gender-specialties-label">
                {GENDER_PREFERENCES.map((g) => (
                  <button type="button" key={g.value} className="chip" aria-pressed={genderSpecialties.includes(g.value)} style={{ background: genderSpecialties.includes(g.value) ? 'var(--warm-terracotta)' : '#F0EBE0', color: genderSpecialties.includes(g.value) ? 'white' : 'var(--warm-sage)' }} onClick={() => toggle(genderSpecialties, setGenderSpecialties, g.value)}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field"><label>Years of experience</label><input className="inp" type="number" min={0} value={yearsOfExperience} onChange={(e) => setYearsOfExperience(e.target.value)} /></div>
            <div className="field">
              <label>Professional biography (optional)</label>
              <textarea className="inp" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A sentence or two about your approach, in your own words." />
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" disabled={busy} onClick={back}>Back</button>
              <button className="btn full" disabled={busy} onClick={() => saveAndAdvance({
                specialties, modalities: splitCsv(modalities), accepted_ages: acceptedAges,
                languages: splitCsv(languages), gender_specialties: genderSpecialties,
                years_of_experience: yearsOfExperience ? Number(yearsOfExperience) : null,
                bio: bio || null,
              }, nextStep(step))}>{busy ? 'Saving…' : 'Continue'}</button>
            </div>
          </div>
        )}

        {step === 'practice_information' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>Your primary office, plus any additional locations you see patients at. Routing uses whichever office is nearest to each patient.</p>
            <div className="form-grid">
              <div className="field">
                <label>Session format</label>
                <select className="inp" value={sessionFormat} onChange={(e) => setSessionFormat(e.target.value)}>
                  <option value="either">Either</option>
                  <option value="virtual">Virtual</option>
                  <option value="in_person">In person</option>
                </select>
              </div>
              <div className="field" style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 8 }}>
                <input type="checkbox" id="virtualOnly" checked={virtualOnly} onChange={(e) => setVirtualOnly(e.target.checked)} />
                <label htmlFor="virtualOnly" style={{ margin: 0 }}>Virtual only (no in-person office)</label>
              </div>
            </div>

            <h3 style={{ marginBottom: 6 }}>Primary office</h3>
            <div className="form-grid">
              <div className="field"><label>Country</label><input className="inp" value={country} onChange={(e) => setCountry(e.target.value)} /></div>
              <div className="field"><label>State / Province</label><input className="inp" value={stateProvince} onChange={(e) => setStateProvince(e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label>City</label><input className="inp" value={city} onChange={(e) => setCity(e.target.value)} /></div>
              <div className="field"><label>Postal / ZIP code</label><input className="inp" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} /></div>
            </div>
            {!virtualOnly && (
              <div className="field">
                <label>Maximum travel distance</label>
                <select className="inp" value={serviceRadiusKm} onChange={(e) => setServiceRadiusKm(e.target.value)}>
                  <option value="">No limit</option>
                  <option value="10">10 km</option>
                  <option value="20">20 km</option>
                  <option value="50">50 km</option>
                </select>
              </div>
            )}
            <div className="field">
              <label id="service-types-label">Populations served</label>
              <div className="chip-list" role="group" aria-labelledby="service-types-label">
                {SERVICE_TYPES.map((s) => (
                  <button type="button" key={s.value} className="chip" aria-pressed={serviceTypes.includes(s.value)} style={{ background: serviceTypes.includes(s.value) ? 'var(--warm-terracotta)' : '#F0EBE0', color: serviceTypes.includes(s.value) ? 'white' : 'var(--warm-sage)' }} onClick={() => toggle(serviceTypes, setServiceTypes, s.value)}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <h3 style={{ marginBottom: 6, marginTop: 18 }}>Additional locations (optional)</h3>
            {locations.length > 0 && (
              <div className="row-list" style={{ marginBottom: 10 }}>
                {locations.map((l) => (
                  <div key={l.id} className="row">
                    <span>{[l.street_address, l.city, l.state_province, l.country].filter(Boolean).join(', ') || 'Location'}</span>
                    <button className="btn small outline" onClick={() => removeLocation(l.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            <div className="form-grid">
              <div className="field"><label>Street address</label><input className="inp" value={newLocStreet} onChange={(e) => setNewLocStreet(e.target.value)} /></div>
              <div className="field"><label>City</label><input className="inp" value={newLocCity} onChange={(e) => setNewLocCity(e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label>State / Province</label><input className="inp" value={newLocState} onChange={(e) => setNewLocState(e.target.value)} /></div>
              <div className="field"><label>Postal / ZIP code</label><input className="inp" value={newLocPostal} onChange={(e) => setNewLocPostal(e.target.value)} /></div>
            </div>
            <div className="field"><label>Country</label><input className="inp" value={newLocCountry} onChange={(e) => setNewLocCountry(e.target.value)} /></div>
            <button className="btn small outline" disabled={addingLocation || !newLocCity} onClick={addLocation} style={{ marginBottom: 12 }}>
              {addingLocation ? 'Adding…' : '+ Add location'}
            </button>

            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" disabled={busy} onClick={back}>Back</button>
              <button className="btn full" disabled={busy} onClick={() => saveAndAdvance({
                session_format: sessionFormat,
                country: country || null, state_province: stateProvince || null,
                city: city || null, postal_code: postalCode || null, virtual_only: virtualOnly,
                service_radius_km: serviceRadiusKm ? Number(serviceRadiusKm) : null, service_types: serviceTypes,
              }, nextStep(step))}>{busy ? 'Saving…' : 'Continue'}</button>
            </div>
          </div>
        )}

        {step === 'insurance_pricing' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>What you accept, so we can route the right patients to you.</p>
            <div className="field">
              <label id="insurance-types-label">Insurance types accepted</label>
              <div className="chip-list" role="group" aria-labelledby="insurance-types-label">
                {INSURANCE_TYPES.map((t) => (
                  <button type="button" key={t.value} className="chip" aria-pressed={insuranceTypesAccepted.includes(t.value)} style={{ background: insuranceTypesAccepted.includes(t.value) ? 'var(--warm-terracotta)' : '#F0EBE0', color: insuranceTypesAccepted.includes(t.value) ? 'white' : 'var(--warm-sage)' }} onClick={() => toggle(insuranceTypesAccepted, setInsuranceTypesAccepted, t.value)}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {insuranceTypesAccepted.some((t) => t !== 'self_pay') && (
              <div className="field">
                <label id="carriers-label">Carriers accepted</label>
                <div className="chip-list" role="group" aria-labelledby="carriers-label">
                  {INSURANCE_CARRIERS.map((c) => (
                    <button type="button" key={c.value} className="chip" aria-pressed={insuranceAccepted.includes(c.value)} style={{ background: insuranceAccepted.includes(c.value) ? 'var(--warm-terracotta)' : '#F0EBE0', color: insuranceAccepted.includes(c.value) ? 'white' : 'var(--warm-sage)' }} onClick={() => toggle(insuranceAccepted, setInsuranceAccepted, c.value)}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="field">
              <label>Accepting new insurance patients?</label>
              <select className="inp" value={acceptingNewInsurancePatients} onChange={(e) => setAcceptingNewInsurancePatients(e.target.value)}>
                {ACCEPTING_NEW_INSURANCE_PATIENTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <h3 style={{ marginBottom: 6, marginTop: 18 }}>Self-pay pricing</h3>
            <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="acceptingSelfPay" checked={acceptingSelfPay} onChange={(e) => setAcceptingSelfPay(e.target.checked)} />
              <label htmlFor="acceptingSelfPay" style={{ margin: 0 }}>Accepting self-pay patients</label>
            </div>
            {acceptingSelfPay && (
              <>
                <div className="form-grid">
                  <div className="field"><label>Self-pay session fee — minimum ($)</label><input className="inp" type="number" min={0} value={selfPayPriceMin} onChange={(e) => setSelfPayPriceMin(e.target.value)} /></div>
                  <div className="field"><label>Self-pay session fee — maximum ($)</label><input className="inp" type="number" min={0} value={selfPayPriceMax} onChange={(e) => setSelfPayPriceMax(e.target.value)} /></div>
                </div>
                <div className="field"><label>Intake session fee (optional, if different)</label><input className="inp" type="number" min={0} value={intakeSessionFee} onChange={(e) => setIntakeSessionFee(e.target.value)} /></div>
                <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" id="slidingScale" checked={slidingScaleAvailable} onChange={(e) => setSlidingScaleAvailable(e.target.checked)} />
                  <label htmlFor="slidingScale" style={{ margin: 0 }}>Sliding scale available</label>
                </div>
              </>
            )}
            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" disabled={busy} onClick={back}>Back</button>
              <button className="btn full" disabled={busy} onClick={() => saveAndAdvance({
                insurance_types_accepted: insuranceTypesAccepted,
                insurance_accepted: insuranceAccepted,
                accepting_new_insurance_patients: acceptingNewInsurancePatients,
                accepting_self_pay: acceptingSelfPay,
                self_pay_price_min_cents: acceptingSelfPay && selfPayPriceMin ? Math.round(Number(selfPayPriceMin) * 100) : null,
                self_pay_price_max_cents: acceptingSelfPay && selfPayPriceMax ? Math.round(Number(selfPayPriceMax) * 100) : null,
                // Kept in sync with the range's minimum so the existing
                // budget-matching signal (routing_signals/budget.py) needs no changes.
                self_pay_price_cents: acceptingSelfPay && selfPayPriceMin ? Math.round(Number(selfPayPriceMin) * 100) : null,
                intake_session_fee_cents: acceptingSelfPay && intakeSessionFee ? Math.round(Number(intakeSessionFee) * 100) : null,
                sliding_scale_available: slidingScaleAvailable,
              }, nextStep(step))}>{busy ? 'Saving…' : 'Continue'}</button>
            </div>
          </div>
        )}

        {step === 'scheduling_availability' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>This feeds when patients can actually book with you — not a calendar itself, just your general working pattern.</p>
            <div className="field">
              <label id="working-days-label">Working days</label>
              <div className="chip-list" role="group" aria-labelledby="working-days-label">
                {WEEKDAYS.map((d) => (
                  <button type="button" key={d.value} className="chip" aria-pressed={workingDays.includes(d.value)} style={{ background: workingDays.includes(d.value) ? 'var(--warm-terracotta)' : '#F0EBE0', color: workingDays.includes(d.value) ? 'white' : 'var(--warm-sage)' }} onClick={() => toggle(workingDays, setWorkingDays, d.value)}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-grid">
              <div className="field"><label>Working hours — start</label><input className="inp" type="number" min={0} max={23} value={workStartHour} onChange={(e) => setWorkStartHour(e.target.value)} /></div>
              <div className="field"><label>Working hours — end</label><input className="inp" type="number" min={1} max={24} value={workEndHour} onChange={(e) => setWorkEndHour(e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label>Session duration (minutes)</label><input className="inp" type="number" min={15} value={sessionDurationMinutes} onChange={(e) => setSessionDurationMinutes(e.target.value)} /></div>
              <div className="field"><label>Break duration (minutes, optional)</label><input className="inp" type="number" min={0} value={breakDurationMinutes} onChange={(e) => setBreakDurationMinutes(e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label>Max sessions per day (optional)</label><input className="inp" type="number" min={1} value={maxSessionsPerDay} onChange={(e) => setMaxSessionsPerDay(e.target.value)} /></div>
              <div className="field"><label>Max sessions per week (optional)</label><input className="inp" type="number" min={1} value={maxSessionsPerWeek} onChange={(e) => setMaxSessionsPerWeek(e.target.value)} /></div>
            </div>

            <div className="field">
              <label>Current capacity</label>
              <select className="inp" value={capacityStatus} onChange={(e) => setCapacityStatus(e.target.value)}>
                <option value="open">Open</option>
                <option value="limited">Limited</option>
                <option value="standby">Not taking new patients right now</option>
              </select>
            </div>
            <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="accepting" checked={acceptingNewClients} onChange={(e) => setAcceptingNewClients(e.target.checked)} />
              <label htmlFor="accepting" style={{ margin: 0 }}>I'm accepting new patients</label>
            </div>
            <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="pauseNewPatients" checked={pauseNewPatients} onChange={(e) => setPauseNewPatients(e.target.checked)} />
              <label htmlFor="pauseNewPatients" style={{ margin: 0 }}>Pause new patients temporarily</label>
            </div>
            <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="waitlist" checked={waitlistEnabled} onChange={(e) => setWaitlistEnabled(e.target.checked)} />
              <label htmlFor="waitlist" style={{ margin: 0 }}>Offer a waitlist when full</label>
            </div>
            <div className="form-grid">
              <div className="field"><label>Average wait time (days, optional)</label><input className="inp" type="number" min={0} value={averageWaitTimeDays} onChange={(e) => setAverageWaitTimeDays(e.target.value)} /></div>
              <div className="field"><label>Maximum active caseload (optional)</label><input className="inp" type="number" min={0} value={maxActiveCaseload} onChange={(e) => setMaxActiveCaseload(e.target.value)} /></div>
            </div>

            <h3 style={{ marginBottom: 6, marginTop: 18 }}>Vacation &amp; holidays</h3>
            {timeOffDates.length > 0 && (
              <div className="row-list" style={{ marginBottom: 10 }}>
                {timeOffDates.map((t, i) => (
                  <div key={i} className="row">
                    <span>{t.label || 'Time off'}: {t.start_date} – {t.end_date}</span>
                    <button className="btn small outline" onClick={() => removeTimeOff(i)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            <div className="form-grid">
              <div className="field"><label>Start date</label><input className="inp" type="date" value={newTimeOffStart} onChange={(e) => setNewTimeOffStart(e.target.value)} /></div>
              <div className="field"><label>End date</label><input className="inp" type="date" value={newTimeOffEnd} onChange={(e) => setNewTimeOffEnd(e.target.value)} /></div>
            </div>
            <div className="field"><label>Label (optional)</label><input className="inp" value={newTimeOffLabel} onChange={(e) => setNewTimeOffLabel(e.target.value)} placeholder="Vacation, holiday closure…" /></div>
            <button className="btn small outline" disabled={!newTimeOffStart || !newTimeOffEnd} onClick={addTimeOff} style={{ marginBottom: 12 }}>+ Add time off</button>

            <h3 style={{ marginBottom: 6, marginTop: 18 }}>Connect your calendar</h3>
            <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
              ATUUNE isn't a calendar app — it syncs with the one you already use. Paste your calendar's
              secret iCal address (Google Calendar, Outlook, and Apple Calendar all provide this in their
              sharing settings) and we'll import your busy times so patients only ever see real openings.
              You can finish setup without connecting a calendar, but you won't be bookable by patients until you do.
            </p>
            <div className="field"><label>Calendar iCal URL</label><input className="inp" value={icsUrl} onChange={(e) => setIcsUrl(e.target.value)} placeholder="https://calendar.google.com/calendar/ical/…/basic.ics" /></div>
            {!calendarConnected && icsUrl && (
              <div className="field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
                <input type="checkbox" id="calendarConsent" checked={calendarConsent} onChange={(e) => setCalendarConsent(e.target.checked)} style={{ marginTop: 3 }} />
                <label htmlFor="calendarConsent" style={{ margin: 0, fontSize: 13 }}>
                  I consent to ATUUNE reading my calendar's busy/free times to power scheduling.
                </label>
              </div>
            )}
            {calendarSyncNotice && <p className="muted" style={{ fontSize: 13 }}>{calendarSyncNotice}</p>}
            {!calendarConnected && icsUrl && (
              <button className="btn outline" disabled={busy || !calendarConsent} onClick={connectCalendar} style={{ marginBottom: 12 }}>{busy ? 'Connecting…' : 'Connect & Sync'}</button>
            )}
            <div className="field">
              <label>Other calendars</label>
              <div className="chip-list">
                {CALENDAR_CONNECTOR_TYPES.filter((c) => c !== 'ics').map((c) => (
                  <span key={c} className="chip" style={{ opacity: 0.5, cursor: 'not-allowed' }} title="Coming soon">
                    {CALENDAR_CONNECTOR_LABELS[c] || c} {!IMPLEMENTED_CALENDAR_CONNECTORS.has(c) && '(Coming Soon)'}
                  </span>
                ))}
              </div>
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" disabled={busy} onClick={back}>Back</button>
              <button className="btn full" disabled={busy} onClick={() => saveAndAdvance({
                working_days: workingDays,
                session_duration_minutes: sessionDurationMinutes ? Number(sessionDurationMinutes) : 50,
                max_sessions_per_day: maxSessionsPerDay ? Number(maxSessionsPerDay) : null,
                max_sessions_per_week: maxSessionsPerWeek ? Number(maxSessionsPerWeek) : null,
                work_start_hour: workStartHour ? Number(workStartHour) : 9,
                work_end_hour: workEndHour ? Number(workEndHour) : 17,
                break_duration_minutes: breakDurationMinutes ? Number(breakDurationMinutes) : null,
                pause_new_patients: pauseNewPatients,
                time_off_dates: timeOffDates,
                capacity_status: capacityStatus,
                accepting_new_clients: acceptingNewClients,
                waitlist_enabled: waitlistEnabled,
                average_wait_time_days: averageWaitTimeDays ? Number(averageWaitTimeDays) : null,
                max_active_caseload: maxActiveCaseload ? Number(maxActiveCaseload) : null,
              }, nextStep(step))}>{busy ? 'Saving…' : 'Continue'}</button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div>
            <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>Here's what we have — you can come back and update any of this later from your dashboard.</p>
            <div className="row-list" style={{ marginBottom: 14 }}>
              <div className="row"><span>Name</span><span className="muted">{name}</span></div>
              <div className="row"><span>Credentials</span><span className="muted">{credentials || '—'}</span></div>
              <div className="row"><span>Years of experience</span><span className="muted">{yearsOfExperience || '—'}</span></div>
              <div className="row"><span>Languages</span><span className="muted">{languages || '—'}</span></div>
              <div className="row"><span>Specialties</span><span className="muted">{specialties.join(', ') || '—'}</span></div>
              <div className="row"><span>Modalities</span><span className="muted">{modalities || '—'}</span></div>
              <div className="row"><span>Age groups served</span><span className="muted">{acceptedAges.map((a) => AGE_LABELS[a] || a).join(', ') || '—'}</span></div>
              <div className="row"><span>License</span><span className="muted">{npiNumber || licenseNumber ? 'Submitted' : 'Not yet provided'}</span></div>
              <div className="row"><span>Credential documents</span><span className="muted">{Object.keys(uploadedDocs).length > 0 ? `${Object.keys(uploadedDocs).length} on file` : 'None on file'}</span></div>
              <div className="row"><span>Insurance accepted</span><span className="muted">{insuranceAccepted.join(', ') || '—'}</span></div>
              <div className="row"><span>Self-pay</span><span className="muted">{acceptingSelfPay ? (selfPayPriceMin ? `$${selfPayPriceMin}–${selfPayPriceMax || selfPayPriceMin}` : 'Yes') : 'Not accepting self-pay'}</span></div>
              <div className="row"><span>Session format</span><span className="muted">{sessionFormat}</span></div>
              <div className="row"><span>Primary location</span><span className="muted">{[city, stateProvince, country].filter(Boolean).join(', ') || '—'}</span></div>
              <div className="row"><span>Additional locations</span><span className="muted">{locations.length}</span></div>
              <div className="row"><span>Working hours</span><span className="muted">{workStartHour}:00–{workEndHour}:00</span></div>
              <div className="row"><span>Calendar</span><span className="muted">{calendarConnected ? 'Connected' : 'Not connected'}</span></div>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn outline" disabled={busy} onClick={back}>Back</button>
              <button className="btn full" disabled={busy} onClick={finish}>{busy ? 'Finishing…' : 'Finish setup'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
